import * as React from 'react';
import { Select } from 'antd';
import { connect } from 'react-redux';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';

// Both registered in field_techs_reducer's initial state. The saved value is looked up into its own
// collection: OWAsyncSelect calls fetchMultiple without one, which would overwrite the slice's
// base list.
const PLANNED_MAINTENANCE_TECHNICIAN_SELECT_COLLECTION = 'plannedMaintenanceTechnicianSelect';
const PLANNED_MAINTENANCE_TECHNICIAN_SELECTED_COLLECTION =
	'plannedMaintenanceTechnicianSelectSelected';

// The supplier internal-team status the PM forms already key the NTE lock off.
export const isInternalTeamSupplier = (supplier) => nullSafeGet('status', supplier) === 'private';

// Only an internal team can take a technician, and only the supplier app can list its techs.
// `supplier` is the record the form loaded, which may still belong to a previously picked supplier.
export const canAssignPlannedMaintenanceTechnician = (
	userType: string,
	supplierFacilityId: number,
	supplier
): boolean =>
	userType === ROLE_TYPES.SUPPLIER &&
	!!supplierFacilityId &&
	nullSafeGet('id', supplier) === supplierFacilityId &&
	isInternalTeamSupplier(supplier);

interface FieldTech {
	email: string;
	supplierContact?: {
		nameGiven?: string;
		nameFamily?: string;
	};
}

interface FieldTechsSlice {
	records: { [email: string]: FieldTech };
	[targetCollectionName: string]: unknown;
}

interface Pagination {
	current: number;
	pageSize: number;
}

interface Sorting {
	sort_by: string;
	order: string;
}

interface FieldTechSearchParams {
	supplierFacilityId: number;
	search?: string;
}

type FetchFieldTechs = (
	params: FieldTechSearchParams,
	targetCollectionName: string,
	pagination: Pagination,
	sorting: Sorting,
	filters: object,
	addToTargetCollection: boolean
) => Promise<FieldTech[]>;

type FetchMultipleFieldTechs = (
	emails: string[],
	targetCollectionName: string
) => Promise<FieldTech[]>;

interface PlannedMaintenanceTechnicianSelectProps {
	supplierFacilityId: number;
	value?: string;
	onChange?: (email: string) => void;
	disabled?: boolean;
	fieldTechs: FieldTechsSlice;
	fetchFieldTechs: FetchFieldTechs;
	fetchMultipleFieldTechs: FetchMultipleFieldTechs;
}

// Supplier-side only: lists the field techs of the selected internal team supplier. Callers key it
// on `supplierFacilityId` so a supplier change remounts it with that supplier's techs.
const PlannedMaintenanceTechnicianSelectComponent: React.FC<
	PlannedMaintenanceTechnicianSelectProps
> = ({
	supplierFacilityId,
	value,
	onChange,
	disabled,
	fieldTechs,
	fetchFieldTechs,
	fetchMultipleFieldTechs,
}) => (
	<OWAsyncSelect
		allowClear={true}
		disabled={disabled}
		placeholder="Supplier's dispatcher will assign a technician"
		value={value}
		onChange={onChange}
		stateSlice={fieldTechs}
		targetCollectionName={PLANNED_MAINTENANCE_TECHNICIAN_SELECT_COLLECTION}
		valueAccessor={(fieldTech: FieldTech) => fieldTech.email}
		fetchData={(searchText, targetCollectionName, pagination, sorting, filters, addToTarget) =>
			fetchFieldTechs(
				{ supplierFacilityId, search: searchText ? searchText.trim() : undefined },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTarget
			)
		}
		fetchMultiple={(emails: string[]) =>
			fetchMultipleFieldTechs(emails, PLANNED_MAINTENANCE_TECHNICIAN_SELECTED_COLLECTION)
		}
		renderRecord={(fieldTech: FieldTech) => (
			<Select.Option key={fieldTech.email} value={fieldTech.email}>
				{nullSafeGet('supplierContact.nameGiven', fieldTech)}{' '}
				{nullSafeGet('supplierContact.nameFamily', fieldTech)} ({fieldTech.email})
			</Select.Option>
		)}
		sortBy={{ sort_by: 'name', order: 'ascend' }}
	/>
);

const mapStateToProps = (state) => ({
	fieldTechs: state.field_techs,
});

const mapDispatchToProps = (dispatch) => ({
	fetchFieldTechs: (params, targetCollectionName, pagination, sorting, filters, addToTarget) =>
		dispatch(
			fieldTechsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTarget
			)
		),
	fetchMultipleFieldTechs: (emails, targetCollectionName) =>
		dispatch(fieldTechsRestCrudThunksForSupplier.readMultiple(emails, targetCollectionName)),
});

export const PlannedMaintenanceTechnicianSelect = connect(
	mapStateToProps,
	mapDispatchToProps
)(PlannedMaintenanceTechnicianSelectComponent);
